import React, { useState } from "react";
import { useCreate } from "@refinedev/core";
import { Create } from "@refinedev/antd";
import { Tabs, Form, Input, Select, Card, Space, Button, Row, Col, Tag, Divider, Typography, Alert, message, Switch, InputNumber, Checkbox, Tooltip } from "antd";
import { InfoCircleOutlined, SaveOutlined, MailOutlined, EditOutlined, PictureOutlined, SettingOutlined, DollarOutlined, EnvironmentOutlined, CheckSquareOutlined, StarOutlined, HomeOutlined, CalendarOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelect } from "@refinedev/core";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const AdvertCreate: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);
  
  const { mutate: createAdvert, isLoading: isCreating } = useCreate();

  const handleFormChange = (changedValues: any, allValues: any) => {
    console.log('Form changed:', changedValues, allValues); // Debug
    setHasChanges(true);
  };

  const handleSave = async () => {
    console.log('🔧 Save button clicked'); // Debug
    
    try {
      console.log('🔧 Validating form fields...'); // Debug
      const values = await form.validateFields();
      console.log('✅ Form validation successful:', values); // Debug
      
      console.log('🔧 Calling createAdvert...'); // Debug
      createAdvert({
        resource: "adverts",
        values,
      }, {
        onSuccess: (data) => {
          console.log('✅ Create success:', data); // Debug
          message.success("Inserat erfolgreich erstellt");
          navigate(`/objekte/${data.data.uid}`);
        },
        onError: (error) => {
          console.error('❌ Create error:', error); // Debug
          message.error(`Fehler beim Erstellen des Inserats: ${error.message || 'Unbekannter Fehler'}`);
        }
      });
    } catch (error) {
      console.error("❌ Validation failed:", error);
      message.error("Formular-Validierung fehlgeschlagen");
    }
  };

  return (
    <Create
      title="Neues Inserat erstellen"
      headerButtons={[
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isCreating}
          // Button ist immer aktiviert beim Erstellen
        >
          Inserat erstellen
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={{
          status: 1,
          star: 0,
          noPersons: 2,
          advertsVat: "19",
          arrival_departure_rules: [],
          checklist: false,
          keycardSystem: false,
          seperatetInvoiceNumber: false,
          percentDepositValue: 0
        }}
      >
        <Tabs defaultActiveKey="1">
          {/* Tab 1: Stammdaten */}
          <TabPane 
            tab={
              <span>
                <EditOutlined />
                Stammdaten
              </span>
            } 
            key="1"
          >
            <StammdatenTab />
          </TabPane>

          {/* Tab 2: Objektbeschreibung */}
          <TabPane 
            tab={
              <span>
                <InfoCircleOutlined />
                Objektbeschreibung
              </span>
            } 
            key="2"
          >
            <ObjektbeschreibungTab />
          </TabPane>

          {/* Tab 3: Ausstattung & Merkmale */}
          <TabPane 
            tab={
              <span>
                <HomeOutlined />
                Ausstattung & Merkmale
              </span>
            } 
            key="4"
          >
            <AusstattungTab />
          </TabPane>

          {/* Tab 4: Entfernungen & Lage */}
          <TabPane 
            tab={
              <span>
                <EnvironmentOutlined />
                Entfernungen & Lage
              </span>
            } 
            key="6"
          >
            <EntfernungenTab />
          </TabPane>

          {/* Tab 5: Zusatzoptionen */}
          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                Zusatzoptionen
              </span>
            } 
            key="7"
          >
            <ZusatzoptionenTab />
          </TabPane>
        </Tabs>
      </Form>
    </Create>
  );
};

// Tab-Komponente für Stammdaten
const StammdatenTab: React.FC = () => {
  const navigate = useNavigate();
  
  const { options: arrivalDepartureOptions, queryResult } = useSelect({
    resource: "arrival-departure-rules",
    optionLabel: "label",
    optionValue: "id",
  });

  const hasRules = arrivalDepartureOptions && arrivalDepartureOptions.length > 0;

  return (
    <div>
      <Title level={4}>Grundinformationen</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="title" label="Titel" rules={[{ required: true, message: "Titel ist erforderlich" }]}>
            <Input placeholder="Inserattitel" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="shorttitle" label="Kurztitel">
            <Input placeholder="Kurzer Titel" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="status" label="Status">
            <Select>
              <Select.Option value={1}>Aktiv</Select.Option>
              <Select.Option value={0}>Inaktiv</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={16}>
          <Form.Item 
            name="arrival_departure_rules" 
            label={
              <Space>
                <CalendarOutlined />
                An-/Abreise-Regeln (Mehrfachauswahl)
                <Tooltip title="Wählen Sie eine oder mehrere Regeln, die automatisch für dieses Objekt gelten sollen.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            extra={
              hasRules 
                ? "Ohne Auswahl sind alle Wochentage für An- und Abreise erlaubt. Sie können mehrere Regeln kombinieren."
                : <Space>
                    <Text type="secondary">Keine Regeln vorhanden.</Text>
                    <Button
                      type="link"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => navigate('/#/an-abreise')}
                    >
                      Regeln erstellen
                    </Button>
                  </Space>
            }
          >
                         <Select
               mode="multiple"
               placeholder={hasRules ? "Regeln auswählen (optional)" : "Keine Regeln verfügbar"}
               allowClear
               showSearch
               disabled={!hasRules}
               maxTagCount="responsive"
               options={arrivalDepartureOptions}
               filterOption={(input, option) =>
                 String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
               }
             />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>Adresse</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={16}>
          <Form.Item name="street" label="Straße">
            <Input placeholder="Straßenname" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="houseNumber" label="Hausnummer">
            <Input placeholder="Nr." />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="zip" label="PLZ">
            <Input placeholder="Postleitzahl" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={16}>
          <Form.Item name="city" label="Stadt">
            <Input placeholder="Stadt" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>Kapazität</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item name="noPersons" label="Max. Personen">
            <InputNumber min={1} max={20} placeholder="Maximale Personenzahl" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="totalSize" label="Größe">
            <Input placeholder="z.B. 75 qm" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>Firmendaten</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item name="companyName" label="Firmenname">
            <Input placeholder="Firma" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="companyOwnerName" label="Inhaber">
            <Input placeholder="Name des Inhabers" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="ustid" label="USt-IdNr.">
            <Input placeholder="DE123456789" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="advertsVat" label="MwSt-Satz">
            <Select>
              <Select.Option value="7">7%</Select.Option>
              <Select.Option value="19">19%</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

// Tab-Komponente für Objektbeschreibung
const ObjektbeschreibungTab: React.FC = () => {
  return (
    <div>
      <Title level={4}>Beschreibungstexte</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Form.Item name="summary" label="Kurzbeschreibung">
            <Input.TextArea rows={3} placeholder="Kurze Beschreibung des Objekts" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item name="accommodation" label="Unterkunftsbeschreibung">
            <Input.TextArea rows={4} placeholder="Detaillierte Beschreibung der Unterkunft" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item name="environment" label="Umgebung">
            <Input.TextArea rows={3} placeholder="Beschreibung der Umgebung und Lage" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item name="features" label="Besonderheiten">
            <Input.TextArea rows={3} placeholder="Besondere Ausstattung und Features" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item name="directions" label="Anfahrtsbeschreibung">
            <Input.TextArea rows={2} placeholder="Wegbeschreibung zum Objekt" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

// Tab-Komponente für Ausstattung & Merkmale
const AusstattungTab: React.FC = () => {
  return (
    <div>
      <Title level={4}>Ausstattung & Merkmale</Title>
      
      <Alert
        message="Ausstattungs-Konfiguration"
        description="Die detaillierte Ausstattungs-Konfiguration über Equipment, Leisure und Suitable-Kategorien kann nach dem Erstellen des Inserats in der Bearbeitung vorgenommen werden."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="noPersons" label="Max. Personen">
            <InputNumber min={1} max={20} placeholder="Anzahl Personen" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="star" label="Sterne-Bewertung">
            <Select placeholder="Sterne auswählen">
              <Select.Option value={0}>Keine Bewertung</Select.Option>
              <Select.Option value={1}>⭐ 1 Stern</Select.Option>
              <Select.Option value={2}>⭐⭐ 2 Sterne</Select.Option>
              <Select.Option value={3}>⭐⭐⭐ 3 Sterne</Select.Option>
              <Select.Option value={4}>⭐⭐⭐⭐ 4 Sterne</Select.Option>
              <Select.Option value={5}>⭐⭐⭐⭐⭐ 5 Sterne</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

// Tab-Komponente für Entfernungen & Lage
const EntfernungenTab: React.FC = () => {
  return (
    <div>
      <Title level={4}>Entfernungen (in km)</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dtTrainStation" label="Bahnhof">
            <InputNumber min={0} placeholder="Entfernung in km" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dtAirport" label="Flughafen">
            <InputNumber min={0} placeholder="Entfernung in km" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dtBus" label="Bushaltestelle">
            <InputNumber min={0} placeholder="Entfernung in km" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dtCityCenter" label="Stadtzentrum">
            <InputNumber min={0} placeholder="Entfernung in km" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dtShopping" label="Einkaufsmöglichkeiten">
            <InputNumber min={0} placeholder="Entfernung in km" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dtDoctor" label="Arzt">
            <InputNumber min={0} placeholder="Entfernung in km" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="dtBakery" label="Bäckerei">
            <InputNumber min={0} placeholder="Entfernung in km" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

// Tab-Komponente für Zusatzoptionen
const ZusatzoptionenTab: React.FC = () => {
  return (
    <div>
      <Title level={4}>Zusatzoptionen</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item name="checklist" valuePropName="checked">
            <Checkbox>Checkliste verwenden</Checkbox>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="keycardSystem" valuePropName="checked">
            <Checkbox>Keycard-System</Checkbox>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="seperatetInvoiceNumber" valuePropName="checked">
            <Checkbox>Separate Rechnungsnummer</Checkbox>
          </Form.Item>
        </Col>
      </Row>
      
      <Divider />
      
      <Title level={4}>Anzahlung</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item name="percentDepositValue" label="Anzahlung (%)">
            <InputNumber min={0} max={100} placeholder="z.B. 30" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="percentDepositText" label="Anzahlungstext">
            <Input placeholder="Text für Anzahlung" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
}; 